import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import Button from '../../components/Button';
import Callout from '../../components/Callout';
import Select from '../../components/Select';
import { WizardStatus } from '../../components/WizardShell';
import { api, type Concept, type ShareAdminView } from '../../lib/api';
import { cx } from '../../lib/cx';

/** Owner-side view of the committee link: the tally and the controls. */
export default function Committee() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ShareAdminView | null>(null);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [expiry, setExpiry] = useState('30');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [share, list] = await Promise.all([api.getShare(id), api.getConcepts(id)]);
      setData(share);
      setConcepts(list.concepts);
    } catch {
      setError('We could not load the committee view.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await api.createShare(id, expiry === 'never' ? null : Number(expiry));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const update = async (token: string, patch: { allowVotes?: boolean; revoked?: boolean }) => {
    if (!id) return;
    setBusy(true);
    try {
      await api.updateShare(id, token, patch);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      setTimeout(() => setCopied(null), 2500);
    } catch {
      // Clipboard is blocked in some contexts; the link is on screen anyway.
    }
  };

  if (error && !data) return <WizardStatus message={error} />;
  if (!data) return <WizardStatus message="Loading…" />;

  const conceptIndex = new Map(concepts.map((c, i) => [c.id, i + 1]));
  const live = data.links.filter((l) => !l.revoked);

  return (
    <AppShell>
      <p className="t-eyebrow text-green-600">Your committee</p>
      <h1 className="t-h1 mt-3 text-ink">Share this and collect their scores</h1>
      <p className="t-body-lg measure mt-4 text-grey-600">
        Anyone with the link can look and score. No account, no sign-in — they open it on
        their phone, tap five numbers, and you see the tally here.
      </p>

      <section className="mt-10">
        <h2 className="t-h2 text-ink">Links</h2>

        {live.length === 0 ? (
          <div className="mt-4 max-w-2xl">
            <Callout title="No link yet">
              Make one and send it to whoever needs to weigh in. You can turn voting off
              or kill the link at any time.
            </Callout>
          </div>
        ) : (
          <ul className="mt-5 flex flex-col gap-4">
            {live.map((l) => (
              <li key={l.token} className="rounded-lg border border-grey-200 bg-white p-5 shadow-sm">
                <p className="t-meta break-all text-ink">{l.url}</p>
                <p className="t-meta mt-2 text-grey-600">
                  <span className="t-numeral">{l.views}</span>{' '}
                  {l.views === 1 ? 'view' : 'views'}
                  {l.expiresAt ? ` · expires ${l.expiresAt.slice(0, 10)}` : ' · no expiry'}
                  {!l.allowVotes && ' · voting closed'}
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button variant="secondary" onClick={() => void copy(l.url)}>
                    {copied === l.url ? 'Copied' : 'Copy link'}
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={busy}
                    onClick={() => void update(l.token, { allowVotes: !l.allowVotes })}
                  >
                    {l.allowVotes ? 'Stop collecting scores' : 'Start collecting again'}
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={busy}
                    onClick={() => void update(l.token, { revoked: true })}
                  >
                    Turn this link off
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex flex-wrap items-end gap-4">
          <div className="w-56">
            <Select
              id="share-expiry"
              label="New link expires"
              options={[
                { value: '7', label: 'In a week' },
                { value: '30', label: 'In a month' },
                { value: '90', label: 'In three months' },
                { value: 'never', label: 'Never' },
              ]}
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
            />
          </div>
          <Button onClick={() => void create()} disabled={busy}>
            Make a link
          </Button>
        </div>
      </section>

      {data.tally.length > 0 && (
        <section className="mt-14">
          <h2 className="t-h2 text-ink">How they scored it</h2>
          <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.tally
              .slice()
              .sort((a, b) => b.average - a.average)
              .map((t) => (
                <li key={t.target} className="rounded-lg border border-grey-200 bg-white p-5 shadow-sm">
                  <p className="t-meta text-grey-600">
                    Option {conceptIndex.get(t.target) ?? '—'}
                  </p>
                  <p className="t-numeral mt-1 text-3xl text-ink">{t.average.toFixed(1)}</p>
                  <span className="mt-2 block h-[6px] w-full rounded-full bg-grey-200">
                    <span
                      className="block h-full rounded-full bg-green-600"
                      style={{ width: `${(t.average / 5) * 100}%` }}
                    />
                  </span>
                  <p className="t-meta mt-2 text-grey-600">
                    from <span className="t-numeral">{t.votes}</span>{' '}
                    {t.votes === 1 ? 'person' : 'people'}
                  </p>
                </li>
              ))}
          </ul>
        </section>
      )}

      {data.voters.length > 0 && (
        <section className="mt-12">
          <h2 className="t-h2 text-ink">
            Who has weighed in{' '}
            <span className="t-numeral text-grey-600">({data.voters.length})</span>
          </h2>
          <ul className="mt-4 flex flex-wrap gap-2">
            {data.voters.map((v) => (
              <li
                key={v.name + (v.email ?? '')}
                className={cx(
                  'min-h-tap inline-flex flex-col justify-center rounded-lg border border-grey-200 bg-white px-4 py-2 shadow-sm',
                )}
              >
                <span className="t-meta font-semibold text-ink">{v.name}</span>
                {v.email && <span className="t-meta text-grey-600">{v.email}</span>}
              </li>
            ))}
          </ul>
          <p className="t-meta measure mt-3 text-grey-600">
            Anyone you sent the link to who is not on this list has not scored it yet.
          </p>
        </section>
      )}

      {data.comments.length > 0 && (
        <section className="mt-12">
          <h2 className="t-h2 text-ink">What they said</h2>
          <ul className="mt-5 flex flex-col gap-4">
            {/* One comment per submission, so de-duplicate across the concepts
                it was attached to. */}
            {Array.from(
              new Map(data.comments.map((c) => [c.name + c.comment, c])).values(),
            ).map((c) => (
              <li key={c.name + c.at} className="rounded-lg border-l-[3px] border-l-grey-300 bg-grey-050 py-4 pr-5 pl-5">
                <p className="t-body measure text-ink">{c.comment}</p>
                <p className="t-meta mt-2 text-grey-600">
                  {c.name} · {c.at.slice(0, 10)}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-12 border-t border-grey-200 pt-8">
        <Button variant="secondary" block onClick={() => navigate(`/project/${id}/concepts`)}>
          Back to the concepts
        </Button>
      </div>
    </AppShell>
  );
}
